import { navigate } from 'gatsby';
import { map } from 'ramda';
import React, { ChangeEventHandler, FC, useEffect, useState } from 'react';
import { animated, useSpring } from 'react-spring';
import { useMeasure } from 'react-use';
import styled from 'styled-components';

import FlexColumn from '@ui/atoms/FlexColumn';
import TextButton, { IButtonProps } from '@ui/atoms/TextButton';

import fullWidth from '@ui/styled/FullWidth';

import useWindowSize from '@hooks/useWindowSize';

import { isBrowser } from '@utils/isBrowser';

import { INavItem } from '@folx-types/dashboard';

import theme from '@theme/FolxTheme';

interface IMobileDashboardNavProps {
	navOpen?: boolean;
	navItems?: INavItem[];
	navClicked?: ChangeEventHandler<HTMLInputElement>;
}

const BaseNavSection = styled(animated.div)`
	margin: 0;
	overflow: hidden;

	> ul {
		overflow: hidden;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
	}
`;

const MemberNav = styled(BaseNavSection)`
	background: ${theme.colors.white};
`;

const NavList = styled.ul`
	display: flex;
	flex-flow: column;
	align-items: center;
	padding: ${theme.spacing.lg} 0 0;

	> li {
		&:nth-last-child(2) {
			margin-bottom: ${theme.spacing.lg};
		}
		&:last-child {
			border-top: 1px solid ${theme.colors.black};
		}
	}
`;

const NavItem = styled.li`
	margin: 0 ${theme.spacing.lg};
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;

	> ${TextButton} {
		padding: ${theme.spacing.sm};
		cursor: pointer;
	}
`;

const NavMenus = styled(FlexColumn)`
	box-shadow: ${theme.shadow.newBox};
	overflow: hidden;
	position: sticky;
	top: 8.3rem;
	right: 0;
	width: 100%;
	z-index: ${theme.zIndexes.header};
	border-end-start-radius: ${theme.radius.smmd};
	border-end-end-radius: ${theme.radius.smmd};
`;

const FullWidthTextButton = fullWidth<IButtonProps>(TextButton);

const MobileDashboardNav: FC<IMobileDashboardNavProps> = ({
	navOpen = false,
	navItems = [],
	navClicked = () => {
		return;
	},
}) => {
	const defaultHeight = 0;

	const [navHeight, setNavHeight] = useState(defaultHeight);
	const [navRef, { height }] = useMeasure<HTMLDivElement>();

	const navExpanded = useSpring({
		height: navOpen ? navHeight : defaultHeight,
	});

	useEffect(() => {
		setNavHeight(height);
	}, [height]);

	const { isTablet, isMobile } = useWindowSize();

	const navList = map((navItem: INavItem) => {
		const activeTab = navItem.pathName;
		const currentPath = window.location.pathname;
		const hasActiveDecoration = activeTab === currentPath;
		return (
			<NavItem role="presentation" key={navItem.pathName}>
				<FullWidthTextButton
					role="menuitem"
					size="lg"
					label={navItem.header}
					color={'black'}
					underline={hasActiveDecoration}
					onClick={() => {
						navigate(navItem.pathName);
						navClicked();
					}}
				/>
			</NavItem>
		);
	}, navItems);

	if (!isBrowser) return <div />;

	return (
		(isTablet || isMobile) && (
			<NavMenus>
				<MemberNav style={navExpanded}>
					<nav ref={navRef}>
						<NavList role="menu" aria-label="Member Dashboard Sections">
							{navList}
							<NavItem role="presentation">
								<FullWidthTextButton
									role="menuitem"
									size="lg"
									label="Sign out"
									onClick={() => navigate('/logout')}
								/>
							</NavItem>
						</NavList>
					</nav>
				</MemberNav>
			</NavMenus>
		)
	);
};

export default MobileDashboardNav;
